/**
 * useChat Hook
 *
 * @module core/chat/hooks
 * @version 1.1
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../auth';
import { useRightPanelSafe } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useModuleStore } from '../../module-store';
import { useOperationalModeStore } from '../../operational-modes';
import { useProject } from '../../projects';
import { chatApi } from '../services/chat-api';
import type { ChatContextPacket, ChatMessage, ChatState } from '../types';

function toChatUiMode(mode: string): 'view' | 'edit' | 'import' | 'analysis' {
  switch (mode) {
    case 'inventory':
      return 'edit';
    case 'plan':
      return 'import';
    case 'analyze':
      return 'analysis';
    default:
      return 'view';
  }
}

function toPermissionsScope(roleName?: string): string {
  switch (roleName) {
    case 'admin':
      return 'project-admin';
    case 'user_high':
    case 'editor':
      return 'project-edit';
    case 'user_medium':
    case 'analyst':
      return 'project-analysis';
    case 'user_low':
    case 'viewer':
      return 'project-read';
    default:
      return 'project-limited';
  }
}

function buildSelectionSummary(selection: { type: string; data: any | null; layerId?: string }) {
  if (selection.type === 'none' || !selection.data) {
    return undefined;
  }

  const data = selection.data as Record<string, unknown>;
  const properties =
    data.properties && typeof data.properties === 'object'
      ? (data.properties as Record<string, unknown>)
      : undefined;

  const selectedEntityId =
    (typeof data.id === 'string' && data.id) ||
    (typeof properties?.id === 'string' && properties.id) ||
    undefined;

  const selectedEntityType =
    selection.layerId ||
    (typeof data.type === 'string' && data.type) ||
    (typeof properties?.entityType === 'string' && properties.entityType) ||
    selection.type;

  const currentSelection: Record<string, unknown> = {
    selectionType: selection.type,
  };

  if (selection.layerId) {
    currentSelection.layerId = selection.layerId;
  }

  if (selectedEntityId) {
    currentSelection.entityId = selectedEntityId;
  }

  if (selectedEntityType) {
    currentSelection.entityType = selectedEntityType;
  }

  if (typeof properties?.name === 'string') {
    currentSelection.name = properties.name;
  }

  if (typeof properties?.code === 'string') {
    currentSelection.code = properties.code;
  }

  return {
    selectedEntityId,
    selectedEntityType,
    currentSelection,
  };
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [state, setState] = useState<ChatState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const { projectId, availableProjects } = useProject();
  const { user } = useAuthStore();
  const rightPanel = useRightPanelSafe();
  const { activeModule } = useModuleStore();
  const { mode, activeLayerSet, selection, rightPanelView } = useOperationalModeStore();

  const buildContext = useCallback((): ChatContextPacket | undefined => {
    if (!projectId) return undefined;

    const activeProject = availableProjects.find((project) => project.id === projectId);
    const selectionSummary = buildSelectionSummary(selection);
    const scope = selectionSummary ? 'selection' : activeModule ? 'module' : 'project';

    return {
      project: {
        id: projectId,
        name: activeProject?.name || 'Proyecto Activo',
        module: activeModule || undefined,
      },
      user: {
        role: user?.role?.name,
        permissionsScope: toPermissionsScope(user?.role?.name),
      },
      ui: {
        activeModule: activeModule || undefined,
        activePanel: rightPanel?.activePanel || undefined,
        mode: toChatUiMode(mode),
        selectedEntityType: selectionSummary?.selectedEntityType,
        selectedEntityId: selectionSummary?.selectedEntityId,
        activeLayer: selection.layerId,
        activeLayerSet,
        rightPanelView,
      },
      observed: {
        warnings: [
          'El context packet actual es parcial y no incluye datasets completos ni geometrías completas.',
        ],
        currentSelection: selectionSummary?.currentSelection,
      },
      calculated: {},
      limits: {
        dataCompleteness: 'partial',
        scope,
        canModifyData: false,
        canExportFinalReports: false,
      },
      capability: {
        phase: '3A',
        enabledDomains: ['urban_network', 'network_associations'],
        disabledDomains: [
          'tree_inventory',
          'environmental_management',
          'waste',
          'public_transport',
          'equipment',
          'census',
          'optimization',
          'final_reports',
        ],
        allowedReasoning: [
          'network_summary',
          'network_validation',
          'topological_warnings',
          'syntax_readiness',
          'association_summary',
          'next_steps',
        ],
        forbiddenReasoning: [
          'module_specific_diagnosis',
          'environmental_diagnosis',
          'waste_service_evaluation',
          'transport_service_evaluation',
          'operational_optimization',
          'automatic_data_modification',
          'final_report_generation',
        ],
      },
      sources: [
        {
          key: 'project-context',
          label: 'Proyecto activo',
          type: 'frontend-state',
        },
        {
          key: 'auth-user',
          label: 'Usuario autenticado',
          type: 'frontend-state',
        },
        {
          key: 'operational-mode-store',
          label: 'Modo operacional y selección',
          type: selectionSummary ? 'user-selection' : 'frontend-state',
        },
        {
          key: 'right-panel-context',
          label: 'Panel derecho activo',
          type: 'frontend-state',
        },
      ],
    };
  }, [activeLayerSet, activeModule, availableProjects, mode, projectId, rightPanel?.activePanel, rightPanelView, selection, user?.role?.name]);

  const loadConversation = useCallback(async () => {
    if (!projectId) {
      setMessages([]);
      setConversationId(null);
      setHasOlderMessages(false);
      setNextBefore(null);
      setState('idle');
      return;
    }

    setMessages([]);
    setConversationId(null);
    setHasOlderMessages(false);
    setNextBefore(null);
    setError(null);

    try {
      const conversation = await chatApi.getCurrentConversation(projectId);
      if (!conversation) {
        setMessages([]);
        setConversationId(null);
        setHasOlderMessages(false);
        setNextBefore(null);
        setState('idle');
        return;
      }

      setMessages(conversation.messages);
      setConversationId(conversation.id);
      setHasOlderMessages(Boolean(conversation.page?.hasMore));
      setNextBefore(conversation.page?.nextBefore || null);
      setState('idle');
    } catch (err) {
      console.error('[useChat] Error al cargar conversación actual:', err);
      setMessages([]);
      setConversationId(null);
      setHasOlderMessages(false);
      setNextBefore(null);
      setState('idle');
    }
  }, [projectId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!projectId) {
        setError('No hay proyecto activo');
        return;
      }

      const trimmedContent = content.trim();
      if (!trimmedContent) {
        return;
      }

      const optimisticMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: trimmedContent,
        timestamp: new Date(),
        projectId,
        conversationId,
      };

      setMessages((prev) => [...prev, optimisticMessage]);
      setState('loading');
      setError(null);

      try {
        const response = await chatApi.sendMessage({
          message: trimmedContent,
          projectId,
          context: buildContext(),
          conversationId: conversationId || undefined,
        });

        setConversationId(response.message.conversationId || conversationId);
        setMessages((prev) => [
          ...prev,
          {
            ...response.message,
            contextSummary: response.message.contextSummary ?? response.contextSummary ?? null,
          },
        ]);
        setState('idle');
      } catch (err) {
        console.error('[useChat] Error al enviar mensaje:', err);
        setError(err instanceof Error ? err.message : 'Error al comunicarse con el asistente');
        setState('error');
        setMessages((prev) => prev.filter((msg) => msg.id !== optimisticMessage.id));
      }
    },
    [buildContext, conversationId, projectId],
  );

  const loadOlderMessages = useCallback(async (): Promise<number> => {
    if (!projectId || !conversationId || !nextBefore || isLoadingOlder) {
      return 0;
    }

    setIsLoadingOlder(true);
    setError(null);

    try {
      const conversation = await chatApi.getConversation(projectId, conversationId, {
        before: nextBefore,
      });

      if (!conversation || conversation.messages.length === 0) {
        setHasOlderMessages(false);
        setNextBefore(null);
        return 0;
      }

      let loadedCount = 0;
      setMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const newMessages = conversation.messages.filter((message) => !existingIds.has(message.id));
        loadedCount = newMessages.length;
        return [...newMessages, ...prev];
      });
      setHasOlderMessages(Boolean(conversation.page?.hasMore));
      setNextBefore(conversation.page?.nextBefore || null);
      return loadedCount;
    } catch (err) {
      console.error('[useChat] Error al cargar mensajes anteriores:', err);
      setError(err instanceof Error ? err.message : 'No fue posible cargar mensajes anteriores');
      return 0;
    } finally {
      setIsLoadingOlder(false);
    }
  }, [conversationId, isLoadingOlder, nextBefore, projectId]);

  const clearMessages = useCallback(async () => {
    if (!projectId || !conversationId) {
      setMessages([]);
      setConversationId(null);
      setHasOlderMessages(false);
      setNextBefore(null);
      setError(null);
      setState('idle');
      return;
    }

    try {
      await chatApi.deleteConversation(projectId, conversationId);
      setMessages([]);
      setConversationId(null);
      setHasOlderMessages(false);
      setNextBefore(null);
      setError(null);
      setState('idle');
    } catch (err) {
      console.error('[useChat] Error al eliminar conversación:', err);
      setError(err instanceof Error ? err.message : 'No fue posible limpiar la conversación');
    }
  }, [conversationId, projectId]);

  const clearError = useCallback(() => {
    setError(null);
    if (state === 'error') {
      setState('idle');
    }
  }, [state]);

  useEffect(() => {
    void loadConversation();
  }, [loadConversation]);

  return {
    messages,
    conversationId,
    state,
    error,
    isLoading: state === 'loading' || state === 'streaming',
    hasOlderMessages,
    isLoadingOlder,
    sendMessage,
    loadOlderMessages,
    clearMessages,
    clearError,
  };
}
